import { isNil } from 'lodash-es'
import { useMemo } from 'react'

import {
  AdditionalPriceOption,
  BasePremium,
  ConditionalCover,
  CoverLevelOption,
  CoverOption,
  getActiveCoverLevelOption,
  Promotion,
  Quote,
} from '../domain'
import {
  getCoverOptionConfig,
  IncludedCoversConfig,
  ProductConfig,
} from '../state/configuration'

export const useIncludedCovers = (
  product: ProductConfig,
  coverLevelOption: CoverLevelOption | undefined,
): IncludedCoversConfig =>
  useMemo(
    () =>
      getCoverOptionConfig(product, coverLevelOption?.coverLevel)?.covers.included ?? [],
    [product, coverLevelOption],
  )

export const useBasePremiums = (quote: Quote | undefined): BasePremium[] => {
  return useMemo(() => {
    const coverLevelOption = getActiveCoverLevelOption(quote)

    return coverLevelOption
      ? coverLevelOption.basePremiumItems.map<BasePremium>((item) => ({
          premium: item.premium,
          totalPremium: item.totalPremium,
          taxAmount: item.taxAmount,
          insurer: item.insurer,
          covers: item.covers,
        }))
      : []
  }, [quote])
}

export const useConditionalCovers = (
  quote: Quote | undefined,
  product: ProductConfig,
): ConditionalCover[] =>
  useMemo(() => {
    const coverLevelOption = getActiveCoverLevelOption(quote)
    const { covers } = getCoverOptionConfig(product, coverLevelOption?.coverLevel) ?? {}

    const activeCovers: Set<string> = new Set(
      coverLevelOption?.basePremiumItems.flatMap((it) => it.covers) ?? [],
    )

    return (
      covers?.conditional?.map((coverCode) => ({
        coverCode,
        active: activeCovers.has(coverCode),
      })) ?? []
    )
  }, [quote, product])

export const useCoverOptions = (
  quote: Quote | undefined,
  product: ProductConfig,
): CoverOption[] => {
  return useMemo(() => {
    const coverLevelOption = getActiveCoverLevelOption(quote)

    if (!coverLevelOption) return []

    const { covers } = getCoverOptionConfig(product, coverLevelOption.coverLevel) ?? {}

    return (
      covers?.optional?.map((cover) => {
        const coverOptionItem = coverLevelOption.coverOptionItems.find(
          (item) => item.coverOption === cover.code,
        )

        return {
          coverOption: cover.code,
          available: !isNil(coverOptionItem),
          active: coverOptionItem?.active,
          insurer: coverOptionItem?.insurer,
          premium: coverOptionItem?.premium ?? 0,
          totalPremium: coverOptionItem?.totalPremium ?? 0,
          taxAmount: coverOptionItem?.taxAmount ?? 0,
          contextualHelp: cover.contextualHelp,
        } satisfies CoverOption
      }) ?? []
    )
  }, [quote, product])
}

export const usePromotion = (quote: Quote | undefined): Promotion | undefined =>
  useMemo(() => getActiveCoverLevelOption(quote)?.promotion, [quote])

export const useAdditionalPremiumBreakdown = (
  quote: Quote | undefined,
): AdditionalPriceOption[] =>
  useMemo(() => {
    if (!quote) return []

    const coverLevelOption = getActiveCoverLevelOption(quote)
    return coverLevelOption?.additionalPremiumBreakdown ?? []
  }, [quote])
